import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { authApi, apiUrl } from 'api';
import { useGlobal } from 'contexts/globalContext';
import { RegisterPayload } from 'types/payloads/RegisterPayload';
import { StoredKeys } from 'types/Auth';
import { Button } from 'components/_new/Button';
import TextField from 'components/_common/forms/TextField';
import Checkbox from 'components/_common/forms/Checkbox';
import { colors } from 'theme';
import GoogleIcon from 'assets/icons/google-logo.svg';
import OfficeIcon from 'assets/icons/office-logo.svg';
import { translatedStaticResourceResolver } from 'utils/translatedStaticResourceResolver';
import { AxiosError } from 'axios';
import { generateAutocompleteId } from 'utils/generate-autocomplete-id';
import LanguageSwitcher from './LanguageSwitcher';

interface RegisterFormObject extends RegisterPayload {
	terms: boolean;
}

const Register: FC = () => {
	const navigate = useNavigate();
	const { i18n, t } = useTranslation();
	const { toastRef } = useGlobal();

	const {
		control,
		handleSubmit,
		setError,
		formState: { errors },
	} = useForm<RegisterFormObject>({
		defaultValues: {
			email: '',
			terms: false,
		},
	});

	const lastVisitedProgramName = window.localStorage.getItem(StoredKeys.ProgramName) as string;

	useEffect(() => {
		if (!lastVisitedProgramName) navigate('/login');
	});

	const {
		mutate,
		isSuccess,
		isLoading: isSubmitting,
	} = useMutation((data: RegisterPayload) => authApi.registerUser(data), {
		onError: (error) => {
			if (error instanceof AxiosError) {
				const data = error.response?.data;
				const message = data?.message;
				if (message) {
					if (message === 'userEmailExists') {
						const translated = t(`auth.messages.${message}`);
						setError('email', {
							type: 'custom',
							message: translated,
						});
					} else {
						toastRef?.current?.show({
							severity: 'error',
							life: 3000,
							summary: t('misc.error'),
							detail: data.message,
						});
					}
				} else {
					toastRef?.current?.show({
						severity: 'error',
						life: 3000,
						summary: t('misc.error'),
						detail: error.message,
					});
				}
			}
		},
	});

	const externalLogin = (type: string) => {
		const language = i18n.language.startsWith('pl') ? 'pl' : 'en';
		window.location.href = `${apiUrl}/auth/${type}/redirect?lng=${language}`;
	};

	const handleExternalLoginClick = (type: string) => {
		externalLogin(type);
	};

	const handleSubmitForm = handleSubmit(({ email }) =>
		mutate({ email, language: i18n.language, programName: lastVisitedProgramName })
	);

	if (isSuccess) {
		return (
			<>
				<div>{t('auth.thankYouMessage')}</div>
				<p>{t('auth.closeTabAndCheckInbox')}</p>
			</>
		);
	}

	return (
		<>
			<Helmet title={t('auth.register')} />
			<form onSubmit={handleSubmitForm} className="flex flex-column row-gap-3">
				<div className="text-sm">
					<span>{t('auth.registerTooltip1')}</span>
					<p>{t('auth.registerTooltip2')}</p>
				</div>

				<TextField
					name="email"
					placeholder={t('auth.inputs.email')}
					control={control}
					required
					autoComplete={generateAutocompleteId('login')}
				/>

				<Checkbox
					name="terms"
					control={control}
					text={
						<label htmlFor="termsOfService" className="text-sm">
							{t('auth.accept')}{' '}
							<a
								href={translatedStaticResourceResolver('terms')}
								target="_blank"
								style={{
									color: errors.terms ? 'inherit' : colors.purplishBlue,
								}}
								rel="noreferrer"
							>
								{t('auth.termsOfService')}
							</a>
						</label>
					}
					required
				/>

				<Button submit label={t('auth.buttons.sendLink')} loading={isSubmitting} block />

				<span className="text-center">{t('misc.or')}</span>

				<div className="flex justify-content-between gap-2">
					<Button
						variant="primary-outlined"
						style={{ borderRadius: '12px' }}
						onClick={() => handleExternalLoginClick('google')}
						label={t('auth.buttons.registerWithOffice')}
						block
						className="p-1 py-2"
					>
						<img src={GoogleIcon} alt="icon" />
						<span style={{ fontSize: '0.7rem' }}>{t('auth.buttons.registerWithGoogle')}</span>
					</Button>
					<Button
						variant="primary-outlined"
						style={{ borderRadius: '12px' }}
						onClick={() => handleExternalLoginClick('microsoft')}
						label={t('auth.buttons.registerWithOffice')}
						block
						className="p-1 py-2"
					>
						<img src={OfficeIcon} alt="icon" />
						<span style={{ fontSize: '0.7rem' }}>{t('auth.buttons.registerWithOffice')}</span>
					</Button>
				</div>

				<div className="flex justify-content-center mt-4">
					<LanguageSwitcher />
				</div>
			</form>
		</>
	);
};

export default Register;
